import { TaskPriority, type TaskParticipant, type TaskResponse } from '@/store/tasks/tasks.dto';
import type { CreateActivityGeneratorPayload } from '@/store/vessels/vessel.dto';

import { RepeatOption } from './constants';
import type { CreateTaskPayload, CreateTaskFormValues } from './CreateEditTaskSheet';

type TaskContext = {
  calendarId: string;
  initialData?: TaskResponse;
  eventId?: string;
};

/**
 * Determines if form values require activity generator (repeatable task)
 */
export function isRepeatableTask(repeat?: string): boolean {
  return repeat !== undefined && repeat !== RepeatOption.NO_REPEAT;
}

/**
 * Maps form values to CreateActivityGeneratorPayload for repeatable tasks
 */
export function mapFormToActivityGenerator(
  values: CreateTaskFormValues,
  participants: { person_id: string; type: string; name: string }[],
  equipment: { id: string; name: string } | null | undefined,
  /** Required by `POST /activity` — see `ensureMaintenanceId`, which is how callers get one. */
  maintenanceId: string,
  responsible?: TaskParticipant | null,
  isPrivate?: boolean,
): CreateActivityGeneratorPayload {
  const startIso = values.startDateTime;

  const visibility = isPrivate ? 'private' : 'public';
  // A private task is still assigned: the caller passes its creator as `responsible`.
  const resolvedParticipants = isPrivate ? [] : participants;
  const resolvedResponsible = responsible ?? null;

  return {
    activity_type: 'task',
    title: values.taskName,
    description: values.description?.trim() || '',
    template_type: (values.taskType?.trim() as 'time_based' | 'deadline_based') || 'time_based',
    status: (values.status as CreateActivityGeneratorPayload['status']) || 'pending',
    priority:
      (values.priority as CreateActivityGeneratorPayload['priority']) || TaskPriority.MEDIUM,
    equipment: equipment || null,
    participants: resolvedParticipants,
    participant_ids: resolvedParticipants.map((p) => p.person_id),
    event_source_type: 'repeatable',
    maintenance_id: maintenanceId,
    time_slot_rules: {
      days: values.repeat === RepeatOption.SPECIFIC_DAYS ? values.days || [] : [],
      duration: `${values.duration || 0}m`,
      run_period: values.repeat || RepeatOption.NO_REPEAT,
      start: startIso,
    },
    visibility,
    assigment_rules: resolvedResponsible
      ? {
          assigment_type: resolvedResponsible.type || 'crew',
          type: 'to_person',
          value: [resolvedResponsible.person_id],
        }
      : null,
  };
}

/**
 * Maps form values to CreateTaskPayload for single tasks
 */
export function mapFormToSingleTask(
  values: CreateTaskFormValues,
  equipment: { id: string; name: string } | null | undefined,
  context: TaskContext,
  responsible: TaskParticipant | null,
  participants?: TaskParticipant[],
  isPrivate?: boolean,
): CreateTaskPayload {
  const startIso = values.startDateTime;
  const deadlineIso = values.deadlineDateTime ?? '';
  const visibility = isPrivate ? 'private' : 'public';
  // A private task is still assigned: the caller passes its creator as `responsible`.
  const resolvedResponsible = responsible ?? null;
  const resolvedParticipants = isPrivate ? [] : participants;

  if (context.initialData) {
    return {
      id: context.initialData.id,
      title: values.taskName,
      description: values.description?.trim() || '',
      location: context.initialData.location,
      type: values.taskType?.trim() || context.initialData.type,
      status: values.status || context.initialData.status,
      priority: values.priority || context.initialData.priority || TaskPriority.MEDIUM,
      start: startIso,
      deadline: deadlineIso,
      reminder: startIso,
      // Kept as it was unless the picker changed it, so a task opened before the
      // equipment list loads doesn't lose its equipment on save.
      equipment:
        values.equipmentId === (context.initialData.equipment?.id ?? '')
          ? context.initialData.equipment
          : (equipment ?? null),
      time_slot: {
        type: context.initialData.time_slot?.type || 'time_based',
        start: startIso,
        end: deadlineIso,
      },
      responsible: resolvedResponsible,
      participants: resolvedParticipants,
      visibility,
      cost: values.cost ?? null,
    };
  }

  // Creating new task
  return {
    title: values.taskName,
    description: values.description?.trim() || '',
    location: {
      name: 'Baku',
      address: 'Some address',
      latitude: 2.0,
      longitude: 1.0,
      map_url: 'https://example.com',
    },
    type: values.taskType?.trim() || 'deadline_based',
    status: values.status || 'pending',
    priority: values.priority || TaskPriority.MEDIUM,
    equipment: equipment?.id ? equipment : null,
    start: startIso,
    deadline: deadlineIso,
    reminder: startIso,
    time_slot: {
      type: 'day_based',
      start: startIso,
      end: deadlineIso,
    },
    responsible: resolvedResponsible,
    participants: resolvedParticipants,
    visibility,
    cost: values.cost ?? null,
    ...(context.eventId ? { event_id: context.eventId } : {}),
  };
}
